import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patchState } from '../../nested-value-getter';
import { OrmService } from '../../schema/orm.service';
import { InvoiceFirestoreService } from '../../services/invoice.firestore.service';
import { InvoiceActions } from './invoice.action';
import { InvoiceStateModel } from './invoice.store';

@State<InvoiceStateModel>({
  name: 'invoices',
  defaults: {
    invoices: [],
    isSubscribed: false,
    isLoading: false,
    filter: undefined,
  },
})
@Injectable()
export class InvoiceState {
  private invoiceService = inject(InvoiceFirestoreService);
  private dbUser = inject(OrmService).userCollection;

  @Action(InvoiceActions.GetAllInvoice)
  async getAllInvoices(ctx: StateContext<InvoiceStateModel>) {
    ctx.patchState({ isLoading: true });
    const invoices = await this.invoiceService.GetAll();
    ctx.patchState({ invoices: invoices.map(({ data, ref }) => ({ ...data, ref })), isLoading: false });
  }
  @Action(InvoiceActions.InitInvoices)
  initInvoices(ctx: StateContext<InvoiceStateModel>, { id }: InvoiceActions.InitInvoices) {
    const state = ctx.getState();
    if (!state.isSubscribed) {
      let off;
      ctx.patchState({ isSubscribed: true, isLoading: true });
      if (id) {
        const { runs } = this.invoiceService.SetQuery({
          filterModel: { userId: { filter: this.dbUser.ref(this.dbUser.id(id)), type: 'equals' } },
        });
        off = this.invoiceService.OnQueryAll(runs, (invoices) => {
          ctx.patchState({
            invoices: invoices
              .filter((invoice): invoice is NonNullable<typeof invoice> => Boolean(invoice))
              .map(({ data, ref }) => ({ ...data, ref })),
            isLoading: false,
          });
        });
      } else {
        off = this.invoiceService.OnGetAll((data) => ctx.dispatch(new InvoiceActions.UpdateInvoiceResult(data)));
      }
      off.catch((err) => ctx.dispatch(new InvoiceActions.InvoiceError(err as Error)));
      ctx.patchState({ off });
    }
  }

  @Action(InvoiceActions.CloseInvoicesConnection)
  closeInvoicesConnection(ctx: StateContext<InvoiceStateModel>) {
    const state = ctx.getState();
    if (state.isSubscribed && state.off) {
      state.off();
    }
    ctx.patchState({ isSubscribed: false });
  }

  @Action(InvoiceActions.UpdateInvoice)
  async updateMachine(ctx: StateContext<InvoiceStateModel>, { invoiceData, ref }: InvoiceActions.UpdateInvoice) {
    await this.invoiceService.Update(ref, invoiceData);
  }

  @Action(InvoiceActions.UpdateInvoiceResult)
  updateInvoiceResult(ctx: StateContext<InvoiceStateModel>, { data }: InvoiceActions.UpdateInvoiceResult) {
    patchState<InvoiceStateModel>(ctx, 'invoices', data);
  }

  @Action(InvoiceActions.QueryInvoice)
  async queryInvoice(ctx: StateContext<InvoiceStateModel>, { filter }: InvoiceActions.QueryInvoice) {
    ctx.patchState({ isLoading: true });
    const invoices = await this.invoiceService.QueryAll(filter.query);
    ctx.patchState({ invoices: invoices.map(({ data, ref }) => ({ ...data, ref })), isLoading: false });
  }
  @Action(InvoiceActions.InvoiceError)
  invoiceError(ctx: StateContext<InvoiceStateModel>, { error }: InvoiceActions.InvoiceError) {
    console.error({ error });
  }

  @Selector()
  static invoices(state: InvoiceStateModel) {
    return state.invoices;
  }

  @Selector()
  static invoiceState(state: InvoiceStateModel) {
    return state;
  }
}
